// A platform admin's way into any organisation or workspace (#701).
//
// The API has always let a superadmin select a context it holds no membership
// in (`auth/service.py::select_context`); the console only ever offered the
// user's own memberships, so a platform admin with none saw "You are not in a
// workspace yet" and had nowhere to go. This is the chooser: every organisation,
// each one's workspaces read on demand from the platform lane
// (`GET /api/v1/orgs/{id}/workspaces`), and an "Enter" on each. The entry is
// audited server-side as the platform's (`platform_admin.context_entered`).
//
// Rendered only for a platform admin — the callers check `user.superadmin` — and
// the routes refuse anyone else whatever the browser shows. What the admin may
// do once inside is not decided here (#702).

import { useState } from "react";

import type { OrgRow, OrgWorkspaceRow } from "../api/customers";
import { messageOf } from "../api/http";
import { orgKindName } from "../api/memberships";
import type { ContextChoice } from "../api/session";
import { Notice } from "./chrome";

export type ListWorkspaces = (orgId: string) => Promise<OrgWorkspaceRow[]>;
export type Enter = (choice: ContextChoice) => Promise<void>;

type OrgWorkspaces =
  | { kind: "closed" }
  | { kind: "reading" }
  | { kind: "read"; workspaces: OrgWorkspaceRow[] }
  | { kind: "failed"; message: string };

/** The whole chooser: every partner and customer, as a list. */
export function PlatformEntry({
  orgs,
  error,
  listWorkspaces,
  onEnter,
}: {
  orgs: OrgRow[] | null;
  error: string | null;
  listWorkspaces: ListWorkspaces;
  onEnter: Enter;
}) {
  return (
    <section className="stack" aria-label="Enter as a platform admin">
      <h2>Enter as a platform admin</h2>
      <p className="muted">
        Any partner, customer or workspace, whether or not you are a member. Each entry is written to
        its audit log.
      </p>
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      {orgs === null && error === null ? (
        <p className="muted" role="status">
          Reading the platform's customers and partners…
        </p>
      ) : null}
      {orgs !== null && orgs.length === 0 ? <p className="muted">No customers or partners yet.</p> : null}
      {orgs !== null && orgs.length > 0 ? (
        <ul className="choices" aria-label="Every partner and customer">
          {orgs.map((org) => (
            <li key={org.org_id} className="platform-entry__org">
              <strong>{org.name}</strong>{" "}
              <small className="muted">{orgKindName(org.kind)}</small>
              <OrgEntry org={org} listWorkspaces={listWorkspaces} onEnter={onEnter} />
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/** One partner's or customer's "Enter", and its workspaces behind a toggle. */
export function OrgEntry({
  org,
  listWorkspaces,
  onEnter,
}: {
  org: OrgRow;
  listWorkspaces: ListWorkspaces;
  onEnter: Enter;
}) {
  const [orgWorkspaces, setOrgWorkspaces] = useState<OrgWorkspaces>({ kind: "closed" });

  // A refusal is not shown here: `chooseContext` dispatches `context-failed`, which the
  // no-context screen and the shell each present once already.
  const enter = (choice: ContextChoice) => {
    void onEnter(choice).catch(() => {});
  };

  const toggle = () => {
    if (orgWorkspaces.kind !== "closed") {
      setOrgWorkspaces({ kind: "closed" });
      return;
    }
    setOrgWorkspaces({ kind: "reading" });
    listWorkspaces(org.org_id).then(
      (workspaces) => setOrgWorkspaces({ kind: "read", workspaces }),
      (failure: unknown) => setOrgWorkspaces({ kind: "failed", message: messageOf(failure) }),
    );
  };

  return (
    <div className="stack stack--tight">
      <div className="actions">
        <button
          type="button"
          className="btn"
          aria-label={`Enter ${org.name}`}
          onClick={() => enter({ org_id: org.org_id })}
        >
          {org.kind === "partner" ? "Enter partner" : "Enter customer"}
        </button>
        <button
          type="button"
          className="link"
          aria-expanded={orgWorkspaces.kind !== "closed"}
          aria-label={`Workspaces of ${org.name}`}
          onClick={toggle}
        >
          {orgWorkspaces.kind === "closed" ? "Show workspaces" : "Hide workspaces"}
        </button>
      </div>
      {orgWorkspaces.kind === "reading" ? (
        <p className="muted" role="status">
          Reading {org.name}'s workspaces…
        </p>
      ) : null}
      {orgWorkspaces.kind === "failed" ? <Notice tone="bad">{orgWorkspaces.message}</Notice> : null}
      {orgWorkspaces.kind === "read" && orgWorkspaces.workspaces.length === 0 ? (
        <p className="muted">{org.name} has no workspace of its own.</p>
      ) : null}
      {orgWorkspaces.kind === "read" && orgWorkspaces.workspaces.length > 0 ? (
        <ul className="platform-entry__workspaces" aria-label={`${org.name}'s workspaces`}>
          {orgWorkspaces.workspaces.map((workspace) => (
            <li key={workspace.workspace_id}>
              {workspace.name}{" "}
              <button
                type="button"
                className="btn"
                aria-label={`Enter ${workspace.name}`}
                onClick={() => enter({ workspace_id: workspace.workspace_id })}
              >
                Enter
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
